import { Link, useParams } from "react-router-dom";

import { format, formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import { AlertCircle, ArrowLeft, Calendar, Flag, MessageSquare, UsersRound } from "lucide-react";

import { ErrorState } from "@/shared/components/data/ErrorState";
import { StatCard } from "@/shared/components/data/StatCard";
import { StatusPill } from "@/shared/components/data/StatusPill";
import { PageHeader } from "@/shared/components/layout/PageHeader";
import { Button } from "@/shared/components/ui/button";
import { Skeleton } from "@/shared/components/ui/skeleton";
import { cn } from "@/shared/lib/utils";
import { Stage, useGetCircleGroupQuery } from "@/store/apis/adminApi.enhanced";

const STAGE_LABEL: Record<Stage, string> = {
  [Stage.Trying]: "Trying",
  [Stage.Fertility]: "Fertilidad",
  [Stage.Loss]: "Pérdida",
  [Stage.Lost]: "Lost",
};

export function CircleGroupDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const { data: group, isLoading, isError, error, refetch } = useGetCircleGroupQuery({ id });

  if (isError) {
    return (
      <>
        <BackLink />
        <div className="overflow-hidden rounded-2xl border bg-card shadow-card-premium">
          <ErrorState error={error} onRetry={() => void refetch()} />
        </div>
      </>
    );
  }

  if (isLoading || !group) {
    return (
      <>
        <BackLink />
        <div className="mb-6 overflow-hidden rounded-2xl border bg-card p-6 shadow-card-premium">
          <div className="flex items-start gap-5">
            <Skeleton className="h-16 w-16 rounded-2xl" />
            <div className="flex-1 space-y-3">
              <Skeleton className="h-6 w-64" />
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-4 w-56" />
            </div>
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <StatCard key={i} label="" value="" loading />
          ))}
        </div>
      </>
    );
  }

  return (
    <>
      <BackLink />

      <PageHeader
        kicker="Comunidad · Grupo"
        title={group.title}
        description={
          <>
            Grupo del Círculo monitorado desde el backoffice. ID{" "}
            <code className="rounded bg-muted/60 px-1.5 py-0.5 font-mono text-[11.5px] text-foreground">
              {group.id}
            </code>
          </>
        }
      />

      {/* Hero card */}
      <section className="mb-6 overflow-hidden rounded-2xl border bg-card shadow-card-premium">
        <div className="flex flex-col gap-5 p-6 sm:flex-row sm:items-center">
          <div
            aria-hidden
            className={cn(
              "flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl shadow-md ring-4 ring-background",
              "font-display text-2xl font-medium tracking-display-tight text-primary-foreground",
            )}
            style={{ background: group.avatar_bg || "#3d8f5f" }}
          >
            {group.title.slice(0, 2).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <StatusPill tone={group.status === "active" ? "success" : "muted"}>
                {group.status === "active" ? "Activo" : "Archivado"}
              </StatusPill>
              {group.stages.map((s) => (
                <span
                  key={s}
                  className="inline-flex rounded-full border border-border/60 bg-muted/50 px-2.5 py-0.5 font-mono text-[10.5px] font-medium text-foreground"
                >
                  {STAGE_LABEL[s]}
                </span>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[12.5px] text-muted-foreground">
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" />
                Creado el{" "}
                <span className="font-medium text-foreground">
                  {format(new Date(group.created_at), "d 'de' MMMM, yyyy", { locale: es })}
                </span>
              </span>
              <span className="inline-flex items-center gap-1.5">
                <MessageSquare className="h-3.5 w-3.5" />
                Última actividad hace{" "}
                <span className="font-medium text-foreground">
                  {formatDistanceToNow(new Date(group.last_activity_at), { locale: es })}
                </span>
              </span>
              {group.archived_at ? (
                <span className="inline-flex items-center gap-1.5 text-destructive">
                  <AlertCircle className="h-3.5 w-3.5" />
                  Archivado el {format(new Date(group.archived_at), "d MMM yyyy", { locale: es })}
                </span>
              ) : null}
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Miembros"
          value={group.member_count.toLocaleString("es-PE")}
          hint="Total actual del grupo"
          icon={<UsersRound className="h-4 w-4" />}
        />
        <StatCard
          label="Mensajes · 7d"
          value={group.messages_count_7d.toLocaleString("es-PE")}
          hint="Últimos 7 días"
          icon={<MessageSquare className="h-4 w-4" />}
        />
        <StatCard
          label="Reportes"
          value={group.reports_count.toLocaleString("es-PE")}
          hint="Totales en este grupo"
          icon={<Flag className="h-4 w-4" />}
        />
        <StatCard
          label="Pendientes"
          value={group.pending_reports_count.toLocaleString("es-PE")}
          hint="Reportes por revisar"
          icon={<AlertCircle className="h-4 w-4" />}
          trend={
            group.pending_reports_count > 0
              ? { value: "atención", direction: "down" }
              : { value: "al día", direction: "flat" }
          }
        />
      </div>

      {/* Nota: no hay endpoint todavía para listar mensajes/reportes */}
      <div className="mt-6 rounded-xl bg-muted/50 p-4">
        <p className="text-[12.5px] leading-relaxed text-muted-foreground">
          <span className="font-medium text-foreground">Próximamente:</span> podrás ver y moderar
          reportes directamente desde esta vista. Por ahora el moderador atiende los reportes desde
          el panel de moderación interna.
        </p>
      </div>
    </>
  );
}

function BackLink() {
  return (
    <Button asChild variant="ghost" size="sm" className="-ml-2 mb-4 text-muted-foreground">
      <Link to="/circulo/grupos">
        <ArrowLeft className="h-3.5 w-3.5" />
        Volver a grupos
      </Link>
    </Button>
  );
}
